import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Row, Col, Table, Tabs, Tab, Badge, InputGroup } from 'react-bootstrap';
import axios from '../../services/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatDateRange } from '../../utils/dateUtils';
import {
  getActiveAgeRules,
  validateTeamRegistration,
  canAddMemberToTeam,
  getMemberAgeStatus,
  getEventReferenceDate,
  getOptionLabel,
  getOptionValue,
  getTeamSizeRule,
  getAllowedOutOfQuota
} from '../../utils/teamAgeValidation';

const computeRegistrationStatus = (eventData) => {
  if (!eventData) return 'open';
  try {
    const now = new Date();
    const registrationDeadlineRaw = eventData.registrationDeadline || eventData.registration_deadline;
    const registrationDeadline = registrationDeadlineRaw ? new Date(registrationDeadlineRaw) : null;
    const allowLateRegistration = eventData.allowLateRegistration === true || eventData.allow_late_registration === true;
    const eventDate = new Date(eventData.eventDate || eventData.event_date);

    if (!isNaN(eventDate.getTime()) && now > eventDate) {
      return 'closed';
    }

    if (registrationDeadline && now > registrationDeadline) {
      return allowLateRegistration ? 'late' : 'closed';
    }

    return 'open';
  } catch (error) {
    console.error('Error checking registration status:', error);
    return 'open';
  }
};

const TeamRegistration = () => {
  const { id, adminAssociationId } = useParams(); // ID dell'evento
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const targetAssociationId = adminAssociationId || (user && user.association_id);
  const isAdminFlow = !!adminAssociationId;

  const handleNavigateBack = () => {
    if (isAdminFlow) {
      navigate(`/admin/events/${id}/participants`);
    } else {
      navigate(`/association/events/${id}`);
    }
  };

  const [event, setEvent] = useState(null);
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([{
    name: '',
    members: [],
    formFields: {} // Campi di iscrizione personalizzati
  }]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [existingRegistrations, setExistingRegistrations] = useState([]);
  const [showExistingTeams, setShowExistingTeams] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState('');
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [regulationAccepted, setRegulationAccepted] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Definita qui per essere disponibile in tutte le closure, anche con return anticipati
  const isManifestazioneEvent = (event?.category || '').toLowerCase() === 'manifestazione';
  const registrationStatus = useMemo(() => computeRegistrationStatus(event), [event]);
  const isRegistrationOpen = registrationStatus !== 'closed';
  const isEditDisabled = submitting || success || !isRegistrationOpen;

  const availableSports = useMemo(() => {
    const uniqueSportsMap = new Map();

    members.forEach(member => {
      const sport = (member.sport || '').trim();
      if (!sport) return;

      const normalizedKey = sport.toLowerCase();
      if (!uniqueSportsMap.has(normalizedKey)) {
        uniqueSportsMap.set(normalizedKey, sport);
      }
    });

    return Array.from(uniqueSportsMap.values()).sort((a, b) => a.localeCompare(b, 'it'));
  }, [members]);

  // Helper: ottieni URL del regolamento
  const getRegulationUrl = () => {
    if (!event?.documents || event.documents.length === 0) return null;
    const docIndex = event.documents.findIndex(doc => {
      const fileName = doc.name || doc.filename || '';
      return fileName.toLowerCase().includes('regolamento');
    });
    if (docIndex === -1) return null;
    const regulationDoc = event.documents[docIndex];
    const docId = regulationDoc._id || regulationDoc.id || docIndex;
    return `/api/events/${id}/documents/${docId}`;
  };

  // Helper: calcola età e formatta data di nascita come gg-mm-aaaa (età)
  const computeAge = (birthDateString) => {
    if (!birthDateString) return null;
    const d = new Date(birthDateString);
    if (isNaN(d)) return null;
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) {
      age--;
    }
    return age;
  };

  const formatBirthWithAge = (birthDateString) => {
    if (!birthDateString) return '-';
    const d = new Date(birthDateString);
    if (isNaN(d)) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const age = computeAge(birthDateString);
    return `${day}-${month}-${year}${age != null ? ` (${age})` : ''}`;
  };

  const getDefaultTeamBaseName = () => {
    const rawCategory = (event?.category || '').trim();
    if (!rawCategory) {
      return isManifestazioneEvent ? 'Partecipanti' : 'Squadra';
    }

    const prettyCategory = rawCategory
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return isManifestazioneEvent ? prettyCategory : prettyCategory;
  };

  const buildDefaultTeamName = (index = 1, existingNames = []) => {
    const baseName = getDefaultTeamBaseName();
    const usedNumbers = new Set(
      existingNames
        .map(name => String(name || '').match(/(\d+)/)?.[1])
        .filter(Boolean)
        .map(Number)
    );

    let nextNumber = index;
    while (usedNumbers.has(nextNumber)) {
      nextNumber += 1;
    }

    return `${baseName} ${nextNumber}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Recupero dettagli evento
        const eventResponse = await axios.get(`/api/events/${id}`);
        const eventData = eventResponse.data.event || eventResponse.data;
        console.log('Event data loaded:', eventData);
        console.log('Event documents:', eventData.documents);
        setEvent(eventData);

        // Recupero membri dell'associazione
        // Dichiarato qui (fuori dall'if) così è accessibile nel setTeams che segue
        let fetchedRegistrations = [];
        if (targetAssociationId) {
          try {
            // Richiedi tutti i membri senza paginazione e con priorità per sport/categoria evento
            const params = new URLSearchParams({
              noPagination: 'true'
            });
            
            // Se l'evento ha una categoria, usala per ordinare i membri
            if (eventData.category) {
              params.append('eventCategory', eventData.category);
            }
            
            const membersResponse = await axios.get(
              `/api/associations/${targetAssociationId}/members?${params.toString()}`
            );
            setMembers(membersResponse.data.members || []);
            setFilteredMembers(membersResponse.data.members || []);

            // Recupero iscrizioni esistenti
            try {
              const registrationsResponse = await axios.get(
                `/api/associations/${targetAssociationId}/events/${id}/teams`
              );

              if (registrationsResponse.data && registrationsResponse.data.teams) {
                fetchedRegistrations = registrationsResponse.data.teams;
                setExistingRegistrations(fetchedRegistrations);
              }
            } catch (regError) {
              console.error('Errore nel recupero delle iscrizioni esistenti:', regError);
              // Potrebbe non esserci ancora nessuna iscrizione
            }

          } catch (err) {
            console.error('Errore nel recupero dei membri:', err);
            setError('Impossibile caricare i membri dell\'associazione. Riprova più tardi.');
          }
        }

        setLoading(false);

        // Imposta nome squadra iniziale con numero progressivo (se vuoto)
        // Nota: fetchedRegistrations viene catturato dalla closure locale (non dallo stato React)
        // per evitare il problema di stale-closure con existingRegistrations
        try {
          const currentFetchedRegistrations = fetchedRegistrations;
          setTeams(prev => {
            if (!prev || prev.length === 0) return prev;
            if (prev[0].name && prev[0].name.trim() !== '') return prev;

            const getNextTeamNumber = () => {
              const nums = [];
              // Da squadre esistenti (usa dati appena scaricati, non lo stato stale)
              (currentFetchedRegistrations || []).forEach(t => {
                if (!t?.name) return;
                const m = String(t.name).match(/(\d+)/);
                if (m) nums.push(parseInt(m[1], 10));
              });
              // Da draft locali
              prev.forEach(t => {
                if (!t?.name) return;
                const m = String(t.name).match(/(\d+)/);
                if (m) nums.push(parseInt(m[1], 10));
              });
              const max = nums.length ? Math.max(...nums) : 0;
              return max + 1;
            };

            const nextNum = getNextTeamNumber();
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              name: buildDefaultTeamName(nextNum, currentFetchedRegistrations.map(t => t?.name).concat(prev.map(t => t?.name)))
            };
            return updated;
          });
        } catch (e) {
          // no-op
        }
      } catch (err) {
        console.error('Errore nel recupero dei dati:', err);
        setError('Impossibile caricare i dati dell\'evento. Riprova più tardi.');
        setLoading(false);
      }
    };

    fetchData();
  }, [id, targetAssociationId]);

  // Inizializza i campi del form in base ai requisiti dell'evento
  useEffect(() => {
    if (event && event.registrationFields && event.registrationFields.length > 0) {
      setTeams(prevTeams => {
        return prevTeams.map(team => {
          // Se i formFields sono già popolati, non sovrascriverli
          if (Object.keys(team.formFields).length > 0) {
            return team;
          }

          // Altrimenti, inizializza i campi
          const formFields = {};
          event.registrationFields.forEach(field => {
            formFields[field.fieldName] = field.fieldType === 'multiselect' ? [] : '';
          });

          return {
            ...team,
            formFields
          };
        });
      });
    }
  }, [event]);

  // Filtra e ordina i membri
  useEffect(() => {
    let filtered = members;
    
    // Applica filtro di ricerca
    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = members.filter(member => {
        const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
        const fiscalCode = (member.fiscal_code || '').toLowerCase();
        const email = (member.email || '').toLowerCase();
        const cardNumber = (member.card_number || '').toLowerCase();
        const sport = (member.sport || '').toLowerCase();

        return fullName.includes(lowerSearch) || 
               fiscalCode.includes(lowerSearch) || 
               email.includes(lowerSearch) ||
               cardNumber.includes(lowerSearch) ||
               sport.includes(lowerSearch);
      });
    }

    // Applica filtro sport
    if (selectedSport) {
      const selectedSportLower = selectedSport.toLowerCase();
      filtered = filtered.filter(member => (member.sport || '').toLowerCase() === selectedSportLower);
    }

    // Applica ordinamento
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'name') {
          aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
          bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
        } else if (sortConfig.key === 'age') {
          aValue = computeAge(a.birth_date) ?? 999;
          bValue = computeAge(b.birth_date) ?? 999;
        } else if (sortConfig.key === 'sport') {
          aValue = (a.sport || '').toLowerCase();
          bValue = (b.sport || '').toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredMembers(filtered);
  }, [searchTerm, selectedSport, members, sortConfig]);

  // Gestisce il cambio di ordinamento
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Componente per l'icona di ordinamento
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="bi bi-arrow-down-up ms-1 text-muted" style={{ fontSize: '0.8rem' }}></i>;
    }
    return sortConfig.direction === 'asc' 
      ? <i className="bi bi-arrow-up ms-1"></i>
      : <i className="bi bi-arrow-down ms-1"></i>;
  };

  const handleAddTeam = () => {
    if (!isRegistrationOpen) {
      setError('Le iscrizioni sono chiuse. Non e possibile aggiungere squadre.');
      return;
    }
    // Inizializza i campi del form per il nuovo team
    const formFields = {};
    if (event && event.registrationFields) {
      event.registrationFields.forEach(field => {
        formFields[field.fieldName] = field.fieldType === 'multiselect' ? [] : '';
      });
    }

    // Calcola prossimo numero progressivo considerando anche iscrizioni esistenti
    const nums = [];
    existingRegistrations.forEach(t => {
      if (!t?.name) return;
      const m = String(t.name).match(/(\d+)/);
      if (m) nums.push(parseInt(m[1], 10));
    });
    teams.forEach(t => {
      if (!t?.name) return;
      const m = String(t.name).match(/(\d+)/);
      if (m) nums.push(parseInt(m[1], 10));
    });
    const nextNum = (nums.length ? Math.max(...nums) : 0) + 1;

    const existingNames = [
      ...existingRegistrations.map(t => t?.name),
      ...teams.map(t => t?.name)
    ];

    setTeams([...teams, {
      name: buildDefaultTeamName(nextNum, existingNames),
      members: [],
      formFields
    }]);

    // Passa automaticamente alla nuova tab
    setActiveTab(teams.length);
  };

  const handleRemoveTeam = (index) => {
    if (!isRegistrationOpen) {
      setError('Le iscrizioni sono chiuse. Non e possibile modificare le squadre.');
      return;
    }
    if (teams.length === 1) {
      setError("Deve rimanere almeno una squadra");
      return;
    }

    const updatedTeams = [...teams];
    updatedTeams.splice(index, 1);
    setTeams(updatedTeams);

    // Aggiusta la tab attiva se necessario
    if (activeTab >= updatedTeams.length) {
      setActiveTab(updatedTeams.length - 1);
    }
  };

  const getTeamMembersData = (memberIds) => {
    return memberIds
      .map(memberId => members.find(m => m.id === memberId))
      .filter(Boolean);
  };

  const handleMemberSelect = (memberId, teamIndex) => {
    const updatedTeams = [...teams];
    const team = updatedTeams[teamIndex];

    if (team.members.includes(memberId)) {
      team.members = team.members.filter(id => id !== memberId);
      setTeams(updatedTeams);
      setError(null);
      return;
    }

    const member = members.find(m => m.id === memberId);
    const sizeRule = getTeamSizeRule(team.formFields);
    if (sizeRule && sizeRule.max !== null && sizeRule.max !== undefined && team.members.length >= sizeRule.max) {
      setError(`Per "${sizeRule.label}" sono ammessi al massimo ${sizeRule.max} partecipanti.`);
      return;
    }

    const rules = getActiveAgeRules(
      event?.registrationFields,
      team.formFields,
      event?.category
    );

    if (rules && member) {
      const currentMembersData = getTeamMembersData(team.members);
      const check = canAddMemberToTeam(
        member,
        currentMembersData,
        rules,
        getEventReferenceDate(event)
      );
      if (!check.allowed) {
        setError(check.reason);
        return;
      }
    }

    team.members.push(memberId);
    setTeams(updatedTeams);
    setError(null);
  };

  const handleTeamNameChange = (value, teamIndex) => {
    const updatedTeams = [...teams];
    updatedTeams[teamIndex].name = value;
    setTeams(updatedTeams);
  };

  const handleFieldChange = (fieldName, value, teamIndex) => {
    const updatedTeams = [...teams];

    // Gestione speciale per i file audio
    if (value instanceof FileList || value instanceof File) {
      // Se è un file audio, lo memorizziamo con informazioni aggiuntive
      if (value instanceof FileList) {
        updatedTeams[teamIndex].formFields[fieldName] = value[0];
      } else {
        updatedTeams[teamIndex].formFields[fieldName] = value;
      }
    } else {
      // Gestione normale per altri campi
      updatedTeams[teamIndex].formFields[fieldName] = value;
    }

    setTeams(updatedTeams);
  };

  const handleMultiSelectChange = (fieldName, value, isChecked, teamIndex) => {
    const updatedTeams = [...teams];
    const currentValues = updatedTeams[teamIndex].formFields[fieldName] || [];

    if (isChecked && !currentValues.includes(value)) {
      updatedTeams[teamIndex].formFields[fieldName] = [...currentValues, value];
    } else if (!isChecked && currentValues.includes(value)) {
      updatedTeams[teamIndex].formFields[fieldName] = currentValues.filter(v => v !== value);
    }

    setTeams(updatedTeams);
  };

  const validateForm = () => {
    // Verifica accettazione regolamento (se presente)
    const regulationUrl = getRegulationUrl();
    if (regulationUrl && !regulationAccepted) {
      setError('Devi accettare il regolamento per procedere con l\'iscrizione');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }

    // Verifica che tutte le squadre abbiano almeno un membro e i campi obbligatori compilati
    for (let i = 0; i < teams.length; i++) {
      const team = teams[i];

      // Verifica il nome della squadra
      if (!team.name.trim()) {
        setError(`La Squadra ${i + 1} deve avere un nome`);
        setActiveTab(i);
        return false;
      }

      // Verifica che ci sia almeno un membro nella squadra
      if (team.members.length === 0) {
        setError(`La Squadra ${i + 1} deve avere almeno un membro`);
        setActiveTab(i);
        return false;
      }

      // Verifica i campi obbligatori
      if (event && event.registrationFields) {
        for (const field of event.registrationFields) {
          if (field.isRequired) {
            const value = team.formFields[field.fieldName];

            // Controllo speciale per file e audio
            if (field.fieldType === 'audio' || field.fieldType === 'file') {
              if (!value || !(value instanceof File)) {
                setError(`${field.fieldType === 'audio' ? 'File audio' : 'Documento'} "${field.fieldLabel}" obbligatorio nella Squadra ${i + 1}`);
                setActiveTab(i);
                return false;
              }
            } else if (!value || (Array.isArray(value) && value.length === 0)) {
              setError(`Campo "${field.fieldLabel}" obbligatorio nella Squadra ${i + 1}`);
              setActiveTab(i);
              return false;
            }
          }
        }
      }

      const teamMembersData = getTeamMembersData(team.members);
      const ageCheck = validateTeamRegistration({
        members: teamMembersData,
        memberCount: team.members.length,
        registrationFields: event?.registrationFields,
        formFields: team.formFields,
        eventCategory: event?.category,
        referenceDate: getEventReferenceDate(event)
      });

      if (!ageCheck.valid) {
        setError(`Squadra ${i + 1}: ${ageCheck.message}`);
        setActiveTab(i);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isRegistrationOpen) {
      setError('Le iscrizioni sono chiuse. Non e possibile completare l\'iscrizione.');
      return;
    }
    setError(null);

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // Invio un'unica richiesta con tutte le squadre: il backend valida tutto
      // prima di creare qualsiasi iscrizione (niente iscrizioni parziali).
      const formData = new FormData();
      formData.append('associationId', targetAssociationId);

      const teamsPayload = teams.map((team, teamIndex) => {
        const teamBasicData = {
          name: team.name,
          memberIds: team.members,
          formData: {}
        };

        Object.entries(team.formFields || {}).forEach(([key, value]) => {
          if (value instanceof File) {
            formData.append(`team_${teamIndex}_${key}`, value);
          } else {
            teamBasicData.formData[key] = value;
          }
        });

        return teamBasicData;
      });

      formData.append('teamsData', JSON.stringify(teamsPayload));

      await axios.post(`/api/events/${id}/teams`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000 // Timeout per l'upload di più file
      });

      setSuccess('Iscrizione completata con successo!');

      // Redirect dopo 2 secondi
      setTimeout(() => {
        handleNavigateBack();
      }, 2000);
    } catch (err) {
      console.error('Errore durante l\'iscrizione:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Errore sconosciuto';
      const errorDetails = err.response?.data?.details || '';
      setError(`Errore durante l'iscrizione: ${errorMsg}${errorDetails ? ` - ${errorDetails}` : ''}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </div>
        <p className="mt-2">Caricamento dati...</p>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">Evento non trovato o non accessibile.</Alert>
        <Button as={Link} to="/association/events" variant="secondary">
          Torna agli Eventi
        </Button>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button as={Link} to={`/association/events/${id}`} variant="outline-secondary">
          &larr; Torna all'Evento
        </Button>

        {existingRegistrations.length > 0 && (
          <Badge bg="info" className="p-2">
            {existingRegistrations.length} {isManifestazioneEvent ? 'partecipanti' : 'squadre'} già {isManifestazioneEvent ? 'iscritti' : 'iscritte'}
            <Button
              variant="link"
              className="text-white ms-2 p-0 align-baseline"
              onClick={() => setShowExistingTeams(!showExistingTeams)}
              style={{ textDecoration: 'underline' }}
            >
              {showExistingTeams ? 'Nascondi' : 'Mostra'}
            </Button>
          </Badge>
        )}
      </div>

      <Card>
        <Card.Header className="bg-primary text-white">
          <h3>{isManifestazioneEvent ? 'Iscrizione Partecipanti' : 'Iscrizione Squadre'}</h3>
          <p className="mb-0">{event.title} - {formatDateRange(event.eventDate || event.event_date, event.eventEndDate || event.event_end_date || event.eventDate || event.event_date)}</p>
        </Card.Header>

        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          {!isRegistrationOpen && (
            <Alert variant="warning" className="mb-3">
              Le iscrizioni sono chiuse. Puoi consultare i dati ma non modificare le iscrizioni.
            </Alert>
          )}

          {/* Visualizzazione delle squadre già iscritte */}
          {showExistingTeams && existingRegistrations.length > 0 && (
            <Card className="mb-4 border-info">
              <Card.Header className="bg-info text-white">
                <h5 className="mb-0">
                  {isManifestazioneEvent ? 'Partecipanti già iscritti' : 'Squadre già iscritte'}
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  {existingRegistrations.map((registration, idx) => (
                    <Col md={6} key={registration._id || idx} className="mb-3">
                      <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">{registration.name}</h6>
                          <Badge bg="secondary">
                            {registration.members?.length || 0} membri
                          </Badge>
                        </Card.Header>
                        <Card.Body>
                          <p className="small text-muted mb-2">
                            Iscrizione: {formatDate(registration.registration_date)}
                          </p>
                          {registration.members && registration.members.length > 0 && (
                            <ul className="list-unstyled">
                              {registration.members.slice(0, 5).map((member, i) => (
                                <li key={i} className="small">
                                  {member.first_name} {member.last_name}
                                </li>
                              ))}
                              {registration.members.length > 5 && (
                                <li className="small text-muted">
                                  ...e altri {registration.members.length - 5}
                                </li>
                              )}
                            </ul>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
                <div className="text-end mt-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    as={Link}
                    to={`/association/events/${id}/teams`}
                  >
                    Gestisci {isManifestazioneEvent ? 'partecipanti' : 'squadre'} &rarr;
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Accettazione Regolamento */}
          {(() => {
            const regulationUrl = getRegulationUrl();
            console.log('Regulation URL:', regulationUrl);
            console.log('Event has documents:', event?.documents?.length || 0);
            return regulationUrl ? (
              <Card className="mb-4 border-warning">
                <Card.Header className="bg-warning bg-opacity-10">
                  <h5 className="mb-0">
                    <i className="bi bi-file-earmark-text me-2"></i>
                    Regolamento Evento
                  </h5>
                </Card.Header>
                <Card.Body>
                  <p className="mb-3">
                    Per partecipare a questo evento è necessario accettare il regolamento.
                  </p>
                  <div className="mb-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      href={regulationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="bi bi-download me-2"></i>
                      Scarica e leggi il regolamento
                    </Button>
                  </div>
                  <Form.Check
                    type="checkbox"
                    id="regulation-acceptance"
                    label={
                      <span>
                        Ho letto e accetto il <strong>regolamento dell'evento</strong>
                        <span className="text-danger ms-1">*</span>
                      </span>
                    }
                    checked={regulationAccepted}
                    onChange={(e) => setRegulationAccepted(e.target.checked)}
                    disabled={isEditDisabled}
                    required
                    className="fs-6"
                  />
                </Card.Body>
              </Card>
            ) : null;
          })()}

          <div className="mb-4">
            <h5>Istruzioni</h5>
            <p>
              {isManifestazioneEvent
                ? 'Aggiungi i partecipanti all\'evento selezionandoli dall\'elenco dei membri.'
                : 'Crea una o più squadre aggiuntive per questo evento e aggiungi membri a ciascuna squadra.'}
            </p>

            {!isManifestazioneEvent && (
              <Button
                variant="success"
                onClick={handleAddTeam}
                disabled={isEditDisabled}
                className="mb-3"
              >
                <i className="bi bi-plus-circle me-2"></i>
                Aggiungi Squadra
              </Button>
            )}
          </div>

          <Form onSubmit={handleSubmit}>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(Number(k))}
              className="mb-4"
            >
              {teams.map((team, index) => (
                <Tab
                  key={index}
                  eventKey={index}
                  title={`${isManifestazioneEvent ? 'Partecipanti' : team.name || `Squadra ${index + 1}`}`}
                >
                  {!isManifestazioneEvent && (
                    <div className="mb-4 d-flex justify-content-between align-items-center">
                      <Form.Group controlId={`teamName-${index}`} className="flex-grow-1 me-3">
                        <Form.Label>Nome Squadra</Form.Label>
                        <Form.Control
                          type="text"
                          value={team.name}
                          onChange={(e) => handleTeamNameChange(e.target.value, index)}
                          disabled={isEditDisabled}
                          required
                        />
                      </Form.Group>

                      {teams.length > 1 && (
                        <Button
                          variant="outline-danger"
                          onClick={() => handleRemoveTeam(index)}
                          disabled={isEditDisabled}
                          style={{ marginTop: '32px' }}
                        >
                          <i className="bi bi-trash"></i>
                          Rimuovi Squadra
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Campi personalizzati dell'evento */}
                  {event.registrationFields && event.registrationFields.length > 0 && (
                    <Card className="mb-4">
                      <Card.Header>Informazioni Richieste</Card.Header>
                      <Card.Body>
                        <Row>
                          {event.registrationFields.map(field => (
                            <Col md={6} key={field.fieldName} className="mb-3">
                              <Form.Group controlId={`${field.fieldName}-${index}`}>
                                <Form.Label>
                                  {field.fieldLabel}
                                  {field.isRequired && <span className="text-danger">*</span>}
                                </Form.Label>

                                {field.fieldType === 'text' && (
                                  <Form.Control
                                    type="text"
                                    value={team.formFields[field.fieldName] || ''}
                                    onChange={(e) => handleFieldChange(field.fieldName, e.target.value, index)}
                                    required={field.isRequired}
                                    disabled={isEditDisabled}
                                  />
                                )}

                                {field.fieldType === 'number' && (
                                  <Form.Control
                                    type="number"
                                    value={team.formFields[field.fieldName] || ''}
                                    onChange={(e) => handleFieldChange(field.fieldName, e.target.value, index)}
                                    required={field.isRequired}
                                    disabled={isEditDisabled}
                                  />
                                )}

                                {field.fieldType === 'select' && (
                                  <Form.Select
                                    value={team.formFields[field.fieldName] || ''}
                                    onChange={(e) => handleFieldChange(field.fieldName, e.target.value, index)}
                                    required={field.isRequired}
                                    disabled={isEditDisabled}
                                  >
                                    <option value="">Seleziona un'opzione</option>
                                    {field.options && field.options.map(option => {
                                      const value = getOptionValue(option);
                                      const label = getOptionLabel(option);
                                      return (
                                        <option key={value} value={value}>{label}</option>
                                      );
                                    })}
                                  </Form.Select>
                                )}

                                {field.fieldType === 'multiselect' && field.options && (
                                  <div>
                                    {field.options.map(option => {
                                      const value = getOptionValue(option);
                                      const label = getOptionLabel(option);
                                      return (
                                        <Form.Check
                                          key={value}
                                          type="checkbox"
                                          id={`${field.fieldName}-${index}-${value}`}
                                          label={label}
                                          checked={(team.formFields[field.fieldName] || []).includes(value)}
                                          onChange={(e) => handleMultiSelectChange(
                                            field.fieldName,
                                            value,
                                            e.target.checked,
                                            index
                                          )}
                                          disabled={isEditDisabled}
                                        />
                                      );
                                    })}
                                  </div>
                                )}

                                {field.fieldType === 'audio' && (
                                  <div>
                                    <div className="d-flex">
                                      <div className="flex-grow-1">
                                        <input
                                          type="file"
                                          className="form-control"
                                          accept="audio/*"
                                          onChange={(e) => handleFieldChange(field.fieldName, e.target.files[0], index)}
                                          required={field.isRequired}
                                          disabled={isEditDisabled}
                                          id={`audio-file-${field.fieldName}-${index}`}
                                        />
                                      </div>
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="ms-2"
                                        onClick={() => document.getElementById(`audio-file-${field.fieldName}-${index}`).click()}
                                        disabled={isEditDisabled}
                                      >
                                        <i className="bi bi-file-music me-1"></i>
                                        Seleziona file
                                      </Button>
                                    </div>
                                    <Form.Text className="text-muted">
                                      Carica il file audio/musica per l'esibizione (MP3, WAV, ecc.)
                                    </Form.Text>
                                    {team.formFields[field.fieldName] && (
                                      <div className="mt-2">
                                        <div className="alert alert-success d-flex align-items-center">
                                          <i className="bi bi-file-music me-2"></i>
                                          <div className="d-flex justify-content-between align-items-center w-100">
                                            <span>{team.formFields[field.fieldName].name}</span>
                                            <div>
                                              <span className="badge bg-success me-2">
                                                {(team.formFields[field.fieldName].size / 1024).toFixed(1)} KB
                                              </span>
                                              <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleFieldChange(field.fieldName, '', index)}
                                                disabled={isEditDisabled}
                                              >
                                                <i className="bi bi-x"></i>
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {field.fieldType === 'file' && (
                                  <div>
                                    <div className="d-flex">
                                      <div className="flex-grow-1">
                                        <input
                                          type="file"
                                          className="form-control"
                                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                                          onChange={(e) => handleFieldChange(field.fieldName, e.target.files[0], index)}
                                          required={field.isRequired}
                                          disabled={isEditDisabled}
                                          id={`file-${field.fieldName}-${index}`}
                                        />
                                      </div>
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="ms-2"
                                        onClick={() => document.getElementById(`file-${field.fieldName}-${index}`).click()}
                                        disabled={isEditDisabled}
                                      >
                                        <i className="bi bi-file-earmark me-1"></i>
                                        Seleziona file
                                      </Button>
                                    </div>
                                    <Form.Text className="text-muted">
                                      Carica un documento richiesto
                                    </Form.Text>
                                    {team.formFields[field.fieldName] && (
                                      <div className="mt-2">
                                        <div className="alert alert-info d-flex align-items-center">
                                          <i className="bi bi-file-earmark me-2"></i>
                                          <div className="d-flex justify-content-between align-items-center w-100">
                                            <span>{team.formFields[field.fieldName].name}</span>
                                            <div>
                                              <span className="badge bg-info me-2">
                                                {(team.formFields[field.fieldName].size / 1024).toFixed(1)} KB
                                              </span>
                                              <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleFieldChange(field.fieldName, '', index)}
                                                disabled={isEditDisabled}
                                              >
                                                <i className="bi bi-x"></i>
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                          ))}
                        </Row>
                      </Card.Body>
                    </Card>
                  )}

                  {(() => {
                    const ageRules = getActiveAgeRules(
                      event.registrationFields,
                      team.formFields,
                      event.category
                    );
                    const sizeRule = getTeamSizeRule(team.formFields);
                    if (!ageRules && !sizeRule) return null;

                    const allowedOutOfQuota = ageRules
                      ? getAllowedOutOfQuota(ageRules, team.members.length)
                      : 0;
                    const quotaRule = ageRules?.outOfQuotaRule;

                    return (
                      <Alert variant="info" className="mb-3">
                        {ageRules && (
                          <>
                            <strong>Vincolo età — {ageRules.categoryLabel}</strong>
                            <div className="small mt-1">
                              In fascia: {ageRules.minAge}-{ageRules.maxAge} anni (alla data evento).
                            </div>
                          </>
                        )}
                        {quotaRule?.enabled ? (
                          <div className="small mt-1">
                            Fuori quota consentiti per {team.members.length} componenti: <strong>{allowedOutOfQuota}</strong>
                            {' '}(da {quotaRule.minMembers} componenti: {quotaRule.baseQuota}, +1 ogni {quotaRule.stepSize} componenti aggiuntivi).
                          </div>
                        ) : ageRules && (
                          <div className="small mt-1">
                            Massimo {ageRules.maxOutOfQuota} partecipanti fuori fascia.
                          </div>
                        )}
                        {sizeRule && (
                          <div className="small mt-1">
                            Formazione &quot;{sizeRule.label}&quot;: {sizeRule.max !== null && sizeRule.max !== undefined
                              ? `esattamente ${sizeRule.max} partecipanti`
                              : `almeno ${sizeRule.min} partecipanti`}.
                          </div>
                        )}
                      </Alert>
                    );
                  })()}

                  <Card>
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Seleziona Membri</span>
                        <Badge bg={team.members.length > 0 ? "success" : "secondary"}>
                          {team.members.length} selezionati
                        </Badge>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {members.length === 0 ? (
                        <Alert variant="warning">
                          Non ci sono membri disponibili per l'iscrizione. Aggiungi membri all'associazione.
                        </Alert>
                      ) : (
                        <>
                          {/* Campo di ricerca */}
                          <Form.Group className="mb-3">
                            <Row className="g-2 mb-2">
                              <Col md={8}>
                                <InputGroup size="lg">
                                  <InputGroup.Text style={{ backgroundColor: '#f8f9fa', borderColor: '#006e39' }}>
                                    <i className="bi bi-search" style={{ color: '#006e39' }}></i>
                                  </InputGroup.Text>
                                  <Form.Control
                                    type="text"
                                    placeholder="Cerca membri per nome, cognome, email o sport..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    disabled={isEditDisabled}
                                    style={{ borderColor: '#006e39' }}
                                  />
                                  {searchTerm && (
                                    <Button
                                      variant="outline-danger"
                                      onClick={() => setSearchTerm('')}
                                      title="Cancella ricerca"
                                    >
                                      <i className="bi bi-x-lg"></i>
                                    </Button>
                                  )}
                                </InputGroup>
                              </Col>
                              <Col md={4}>
                                <Form.Select
                                  size="lg"
                                  value={selectedSport}
                                  onChange={(e) => setSelectedSport(e.target.value)}
                                  disabled={isEditDisabled || availableSports.length === 0}
                                  aria-label="Filtro sport"
                                  style={{ borderColor: '#006e39' }}
                                >
                                  <option value="">Tutti gli sport</option>
                                  {availableSports.map(sport => (
                                    <option key={sport} value={sport}>{sport}</option>
                                  ))}
                                </Form.Select>
                              </Col>
                            </Row>
                            <div className="d-flex justify-content-between align-items-center">
                              {(searchTerm || selectedSport) && (
                                <Form.Text className="text-muted">
                                  <i className="bi bi-funnel me-2"></i>
                                  Trovati {filteredMembers.length} membri su {members.length}
                                  {selectedSport && (
                                    <span className="ms-2">• Sport: "{selectedSport}"</span>
                                  )}
                                </Form.Text>
                              )}
                              {!searchTerm && !selectedSport && members.length > 0 && (
                                <Form.Text className="text-muted">
                                  Totale: {members.length} membri
                                  {event && event.category && (
                                    <span className="ms-2">
                                      • Sport "{event.category}" mostrati per primi
                                    </span>
                                  )}
                                </Form.Text>
                              )}
                            </div>
                          </Form.Group>

                          <Table striped bordered hover responsive>
                            <thead>
                              <tr>
                                <th style={{ width: '50px' }}></th>
                                <th 
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => handleSort('name')}
                                >
                                  Nome
                                  <SortIcon columnKey="name" />
                                </th>
                                <th 
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => handleSort('age')}
                                >
                                  Data di nascita (Età)
                                  <SortIcon columnKey="age" />
                                </th>
                                <th 
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => handleSort('sport')}
                                >
                                  Sport
                                  <SortIcon columnKey="sport" />
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredMembers.length === 0 ? (
                                <tr>
                                  <td colSpan="4" className="text-center text-muted">
                                    Nessun membro trovato con i criteri di ricerca "{searchTerm}"
                                  </td>
                                </tr>
                              ) : (
                                filteredMembers.map(member => {
                                  const ageRules = getActiveAgeRules(
                                    event.registrationFields,
                                    team.formFields,
                                    event.category
                                  );
                                  const sizeRule = getTeamSizeRule(team.formFields);
                                  const ageStatus = getMemberAgeStatus(
                                    member,
                                    ageRules,
                                    getEventReferenceDate(event)
                                  );
                                  const isSelected = team.members.includes(member.id);
                                  const wouldExceedSize = !isSelected
                                    && sizeRule
                                    && sizeRule.max !== null
                                    && sizeRule.max !== undefined
                                    && team.members.length >= sizeRule.max;
                                  const wouldBlock = !isSelected && ageRules && ageStatus === 'out_of_quota' && (() => {
                                    const current = getTeamMembersData(team.members);
                                    const check = canAddMemberToTeam(
                                      member,
                                      current,
                                      ageRules,
                                      getEventReferenceDate(event)
                                    );
                                    return !check.allowed;
                                  })();

                                  return (
                                  <tr 
                                    key={member.id}
                                    className={
                                      event && event.category && 
                                      member.sport && 
                                      member.sport.toLowerCase() === event.category.toLowerCase() 
                                        ? 'table-success' 
                                        : ''
                                    }
                                  >
                                    <td className="text-center">
                                      <Form.Check
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => handleMemberSelect(member.id, index)}
                                        disabled={isEditDisabled || wouldBlock || wouldExceedSize}
                                        title={wouldExceedSize
                                          ? `Massimo ${sizeRule.max} partecipanti per "${sizeRule.label}"`
                                          : (wouldBlock ? 'Limite partecipanti fuori fascia raggiunto' : '')}
                                      />
                                    </td>
                                    <td>
                                      {member.first_name} {member.last_name}
                                      {ageRules && isSelected && ageStatus === 'out_of_quota' && (
                                        <Badge bg="warning" text="dark" className="ms-2">Fuori fascia</Badge>
                                      )}
                                      {ageRules && isSelected && ageStatus === 'in_quota' && (
                                        <Badge bg="success" className="ms-2">In fascia</Badge>
                                      )}
                                      {event && event.category && 
                                       member.sport && 
                                       member.sport.toLowerCase() === event.category.toLowerCase() && (
                                        <Badge bg="success" className="ms-2" pill>
                                          <i className="bi bi-star-fill"></i>
                                        </Badge>
                                      )}
                                    </td>
                                    <td>{formatBirthWithAge(member.birth_date)}</td>
                                    <td>
                                      {member.sport ? (
                                        <Badge 
                                          bg={
                                            event && event.category && 
                                            member.sport.toLowerCase() === event.category.toLowerCase() 
                                              ? 'success' 
                                              : 'secondary'
                                          }
                                        >
                                          {member.sport}
                                        </Badge>
                                      ) : (
                                        '-'
                                      )}
                                    </td>
                                  </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </Table>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Tab>
              ))}
            </Tabs>

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="secondary"
                as={Link}
                to={`/association/events/${id}`}
                className="me-2"
                disabled={submitting}
              >
                Annulla
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isEditDisabled || members.length === 0}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Iscrizione in corso...
                  </>
                ) : (
                  'Completa Iscrizione'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default TeamRegistration;





